/*
 * The left block's menu as data (docs/web.md, "Menu"): groups (headers,
 * always shown) → sections (always shown) → subsections (shown while their
 * section is open). Labels are i18n keys, routes are templates of
 * `app/router.tsx` (`nav.test.ts` checks that every route exists). No React
 * here: the browser-test map (`e2e/tiers/routes.ts`) and the flows' helper
 * (`e2e/fixtures.ts`) read it too.
 *
 * A section not built yet carries `soon: true` and is shown disabled with
 * «скоро»; enabling it is removing that flag once its routes exist.
 */

/** Icons by name; `sidebar.tsx` maps them to Lucide components. */
export type NavIcon =
  | "overview"
  | "dashboard"
  | "servers"
  | "network"
  | "apps"
  | "kubernetes"
  | "dns"
  | "monitoring"
  | "costs"
  | "settings"
  | "projects"
  | "orgCosts"
  | "users"
  | "api";

/** Who may see an item beyond everyone with access to the project. */
export type NavRight = "admin";

export interface NavLink {
  /** Stable id: `servers.all`, `api.keys`. */
  id: string;
  /** i18n key of the label. */
  label: string;
  /** Route template; `$projectId` is the project in view. */
  to: string;
  /** More route templates that belong to this item (detail pages and tabs of its own). */
  also?: readonly string[];
  /** Only this exact address, not the pages below it (`/`, a project's overview). */
  exact?: boolean;
  /** Not built yet: shown disabled with «скоро». */
  soon?: boolean;
  requires?: NavRight;
}

export interface NavSection {
  id: string;
  label: string;
  icon: NavIcon;
  /** A section without subsections is a link itself. */
  to?: string;
  also?: readonly string[];
  exact?: boolean;
  soon?: boolean;
  requires?: NavRight;
  /** Subsections; clicking the section goes to the first one the person may see. */
  children?: readonly NavLink[];
}

export interface NavGroup {
  id: string;
  /** i18n key of the header; the first group of a project has none. */
  label?: string;
  /** `project`: needs a project in view; `org`: the organisation's, never for guests. */
  scope: "project" | "org";
  items: readonly NavSection[];
}

const P = "/projects/$projectId";

export const NAV: readonly NavGroup[] = [
  {
    id: "project",
    scope: "project",
    items: [
      { id: "overview", label: "nav.overview", icon: "overview", to: P, exact: true },
      { id: "dashboard", label: "nav.dashboard", icon: "dashboard", to: `${P}/dashboard` },
    ],
  },
  {
    id: "infrastructure",
    label: "nav.groups.infrastructure",
    scope: "project",
    items: [
      {
        id: "servers",
        label: "nav.servers",
        icon: "servers",
        children: [
          { id: "servers.all", label: "nav.allServers", to: `${P}/servers` },
          { id: "servers.volumes", label: "nav.volumes", to: `${P}/volumes` },
          { id: "servers.images", label: "nav.images", to: `${P}/images` },
          {
            id: "servers.placement",
            label: "nav.placementGroups",
            to: `${P}/placement-groups`,
          },
          { id: "servers.ssh-keys", label: "nav.sshKeys", to: `${P}/ssh-keys` },
        ],
      },
      {
        id: "network",
        label: "nav.networking",
        icon: "network",
        children: [
          { id: "network.networks", label: "nav.networks", to: `${P}/networks` },
          { id: "network.firewalls", label: "nav.firewalls", to: `${P}/firewalls` },
          { id: "network.ips", label: "nav.ips", to: `${P}/ips` },
          { id: "network.floating-ips", label: "nav.floatingIps", to: `${P}/floating-ips` },
          { id: "network.load-balancers", label: "nav.loadBalancers", to: `${P}/load-balancers` },
          { id: "network.certificates", label: "nav.certificates", to: `${P}/certificates` },
        ],
      },
    ],
  },
  {
    id: "services",
    label: "nav.groups.services",
    scope: "project",
    items: [
      {
        id: "apps",
        label: "nav.apps",
        icon: "apps",
        children: [
          { id: "apps.catalogue", label: "nav.appsCatalogue", to: `${P}/apps` },
          { id: "apps.installed", label: "nav.appsInstalled", to: `${P}/apps/installed` },
        ],
      },
      { id: "kubernetes", label: "nav.kubernetes", icon: "kubernetes", soon: true },
      { id: "dns", label: "nav.dns", icon: "dns", soon: true },
    ],
  },
  {
    id: "observe",
    label: "nav.groups.observe",
    scope: "project",
    items: [
      {
        id: "monitoring",
        label: "nav.monitoring",
        icon: "monitoring",
        children: [
          { id: "monitoring.servers", label: "nav.monitoringServers", to: `${P}/monitoring` },
          { id: "monitoring.alerts", label: "nav.alerts", to: `${P}/alerts` },
        ],
      },
      { id: "costs", label: "nav.costs", icon: "costs", to: `${P}/costs` },
    ],
  },
  {
    id: "settings",
    label: "nav.groups.project",
    scope: "project",
    items: [
      {
        id: "settings",
        label: "nav.projectSettings",
        icon: "settings",
        children: [
          { id: "settings.access", label: "nav.projectAccess", to: `${P}/access` },
          { id: "settings.connection", label: "nav.connection", to: `${P}/connection` },
          { id: "settings.catalogue", label: "nav.catalogue", to: `${P}/catalogue` },
        ],
      },
    ],
  },
  {
    id: "org",
    label: "nav.groups.org",
    scope: "org",
    items: [
      { id: "projects", label: "nav.projects", icon: "projects", to: "/", exact: true },
      {
        id: "org-costs",
        label: "nav.orgCosts",
        icon: "orgCosts",
        to: "/costs",
        requires: "admin",
      },
      { id: "users", label: "nav.users", icon: "users", to: "/users", requires: "admin" },
      {
        id: "api",
        label: "nav.apiAccess",
        icon: "api",
        children: [
          {
            id: "api.keys",
            label: "nav.apiKeys",
            to: "/api-access/keys",
            // The key pages and the views shown as tabs of «Ключи» (docs/api-keys.md).
            also: ["/api-access/service-accounts", "/api-access/endpoints", "/api-access/activity"],
          },
          { id: "api.policies", label: "nav.apiPolicies", to: "/api-access/policies" },
          { id: "api.apps", label: "nav.apiApps", to: "/api-access/apps" },
          {
            id: "api.messengers",
            label: "nav.messengers",
            to: "/api-access/messengers",
          },
          { id: "api.approvals", label: "nav.apiApprovals", to: "/api-access/approvals" },
          { id: "api.docs", label: "nav.apiDocs", to: "/api-access/docs" },
        ],
      },
    ],
  },
];

/** What the person may see of the menu. */
export interface NavViewer {
  admin: boolean;
  /** A guest in every project they have (and not an administrator): no organisation group. */
  guestOnly: boolean;
  /** A project is in view: its groups are shown. */
  inProject: boolean;
}

const allowed = (item: { requires?: NavRight }, viewer: NavViewer) =>
  item.requires !== "admin" || viewer.admin;

/** The groups, sections and subsections this person sees, in menu order. */
export function visibleNav(viewer: NavViewer, nav: readonly NavGroup[] = NAV): NavGroup[] {
  return nav
    .filter((group) =>
      group.scope === "project" ? viewer.inProject : viewer.admin || !viewer.guestOnly,
    )
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => allowed(item, viewer))
        .map((item) =>
          item.children
            ? { ...item, children: item.children.filter((child) => allowed(child, viewer)) }
            : item,
        )
        .filter((item) => !item.children || item.children.length > 0),
    }))
    .filter((group) => group.items.length > 0);
}

/** Where a section leads: its own route, or its first subsection's. */
export function sectionTarget(section: NavSection): string | undefined {
  return section.to ?? section.children?.[0]?.to;
}

/** A path with the project's id put back to `$projectId`, without a trailing slash. */
export function templateOf(path: string): string {
  const clean = path.split(/[?#]/)[0] ?? "";
  const trimmed = clean.length > 1 ? clean.replace(/\/+$/, "") : clean;
  return trimmed.replace(/^\/projects\/[^/]+/, P);
}

export interface ActiveItem {
  /** The section that is open (and highlighted when it has no subsections). */
  section: string;
  /** The highlighted subsection, when the section has them. */
  link: string | null;
}

/**
 * The item of the menu the address belongs to: the longest route of an item
 * (or of its `also`) that is the address or above it — a server's page is
 * «Серверы › Все серверы», a key's page «API и интеграции › Ключи».
 */
export function activeItem(path: string, nav: readonly NavGroup[] = NAV): ActiveItem | null {
  const at = templateOf(path);
  const candidates = nav.flatMap((group) =>
    group.items
      .filter((section) => !section.soon)
      .flatMap((section) => [
        { target: section, item: { section: section.id, link: null } },
        ...(section.children ?? []).map((child) => ({
          target: child as NavLink | NavSection,
          item: { section: section.id, link: child.id },
        })),
      ]),
  );
  let best: ActiveItem | null = null;
  let bestLength = -1;
  for (const { target, item } of candidates) {
    if (target.soon || !target.to) continue;
    for (const route of [target.to, ...(target.also ?? [])]) {
      const hit = at === route || (!target.exact && route !== "/" && at.startsWith(`${route}/`));
      if (hit && route.length > bestLength) {
        best = item;
        bestLength = route.length;
      }
    }
  }
  return best;
}

/** The section and subsection labels (i18n keys) → the route a click there opens. */
export function navRoutes(nav: readonly NavGroup[] = NAV): Map<string, string> {
  const out = new Map<string, string>();
  for (const group of nav)
    for (const section of group.items) {
      if (section.soon) continue;
      const target = sectionTarget(section);
      if (target) out.set(section.label, target);
      for (const child of section.children ?? []) out.set(child.label, child.to);
    }
  return out;
}

/** The section a subsection's label belongs to (i18n keys), for opening it first. */
export function parentOf(label: string, nav: readonly NavGroup[] = NAV): string | null {
  for (const group of nav)
    for (const section of group.items)
      if (section.children?.some((child) => child.label === label)) return section.label;
  return null;
}
